import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  BadgeCheck,
  Bell,
  CalendarClock,
  CheckCircle,
  LayoutDashboard,
  Lock,
  Mail,
  ShieldCheck,
  Sparkles,
  Users
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import MarketingTestimonials from "../components/MarketingTestimonials";
import { marketingNavLinks } from "../constants/marketingNav";

const featureSections = [
  {
    id: "authentication",
    label: "Authentication & Users",
    title: "Secure, role-aware access from day one.",
    description:
      "JWT-based auth, RBAC, and profile controls keep the workspace safe while making onboarding effortless for admins and members alike.",
    highlights: [
      "Role-based access (Admin/Member) with guarded routes",
      "Password reset flows with email verification",
      "Session refresh and logout safeguards",
      "Profile management and avatar-ready UI",
    ],
    chips: ["JWT", "RBAC", "Profile", "Reset"],
    visual: "auth",
  },
  {
    id: "projects",
    label: "Project Management",
    title: "Projects shaped to match your delivery rhythm.",
    description:
      "Create, organize, and track projects with clarity. Ownership, membership, and status controls keep momentum visible at every stage.",
    highlights: [
      "Project creation, status tracking, and archiving",
      "Member invitations with role hints",
      "Ownership visibility and change history",
      "Category tags for cross-team clarity",
    ],
    chips: ["Statuses", "Members", "Ownership"],
    visual: "projects",
  },
  {
    id: "tasks",
    label: "Tasks & Boards",
    title: "Kanban-first tasking with assignment, priority, and due dates.",
    description:
      "Drag-and-drop cards across customizable columns. Keep assignees, priorities, and timelines in view with a collaboration-friendly layout.",
    highlights: [
      "Custom columns with drag-and-drop",
      "Assignments, priorities, and due dates",
      "Comments with activity tracking",
      "Inline quick actions for faster triage",
    ],
    chips: ["Kanban", "Priorities", "Due dates"],
    visual: "tasks",
  },
  {
    id: "notifications",
    label: "Notifications",
    title: "Real-time and email notices that never miss the signal.",
    description:
      "WebSocket-powered updates plus email notifications for critical events. Users can mark, clear, and manage alerts without breaking flow.",
    highlights: [
      "Live in-app feed with unread counts",
      "Email delivery for invites and task events",
      "Mark-as-read and bulk cleanup actions",
      "Context-rich alerts for who/what changed",
    ],
    chips: ["Real-time", "Email", "Unread count"],
    visual: "notifications",
  },
  {
    id: "email",
    label: "Email Integration",
    title: "Human-first email touchpoints for every important moment.",
    description:
      "Crafted templates for welcomes, password resets, invites, and assignments keep stakeholders informed with brand-consistent messaging.",
    highlights: [
      "Welcome and onboarding emails",
      "Reset and recovery flows",
      "Project and task invitations",
      "Delivery status visibility for admins",
    ],
    chips: ["Templates", "Deliverability", "Consistency"],
    visual: "email",
  },
];

const boardColumns = [
  {
    id: "planning",
    title: "Planning",
    description: "Next quarter goals.",
    cards: [
      { title: "Define roadmap themes", label: "Design", due: "Mon", members: ["AL"], priority: "High" },
      { title: "Confirm KPI targets", label: "Engineering", due: "Wed", members: ["MK"], priority: "Medium" },
    ],
  },
  {
    id: "in-progress",
    title: "In Progress",
    description: "Active development.",
    cards: [
      { title: "Design onboarding flow", label: "Design", due: "Fri", members: ["JD"], priority: "High" },
      { title: "API credentials vault", label: "Engineering", due: "Thu", members: ["Alex"], priority: "High" },
    ],
  },
  {
    id: "done",
    title: "Done",
    description: "Recently completed.",
    cards: [
      { title: "Security audit prep", label: "Design", due: "Completed", members: ["AL"], priority: "High" },
      { title: "Email templates finalized", label: "QA", due: "Completed", members: ["MK"], priority: "High" },
    ],
  },
];

const AuthVisual = () => (
  <div className="card-marketing reveal-right">
    <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
      <div className="flex items-center gap-2">
        <Lock className="w-5 h-5 text-navy" />
        <span className="font-bold text-gray-900">Access Control</span>
      </div>
      <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-bold rounded-full">Live</span>
    </div>
    <div className="space-y-4">
      {[
        { title: "Admin • Alex Lee", desc: "Full access, can invite & manage roles." },
        { title: "Member • Maria Kim", desc: "Project read/write access." },
      ].map((item) => (
        <div key={item.title} className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <div className="flex-1">
            <p className="font-bold text-gray-900 text-sm">{item.title}</p>
            <p className="text-xs text-gray-500">{item.desc}</p>
          </div>
          <CheckCircle className="w-5 h-5 text-green-500" />
        </div>
      ))}
    </div>
  </div>
);

const ProjectsVisual = () => (
  <div className="card-marketing reveal-right">
    <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
      <div className="flex items-center gap-2">
        <LayoutDashboard className="w-5 h-5 text-navy" />
        <span className="font-bold text-gray-900">Projects</span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      {[
        { title: "Marketing", count: 12, color: "bg-blue-500" },
        { title: "Engineering", count: 8, color: "bg-navy" },
      ].map((cat) => (
        <div key={cat.title} className="p-4 border border-gray-100 rounded-2xl text-center">
          <div className={`w-10 h-10 ${cat.color} rounded-lg mx-auto mb-3 flex items-center justify-center text-white font-bold`}>{cat.title[0]}</div>
          <p className="font-bold text-gray-900 text-sm">{cat.title}</p>
          <p className="text-xs text-gray-500">{cat.count} Projects</p>
        </div>
      ))}
    </div>
  </div>
);

const visuals = {
  auth: <AuthVisual />,
  projects: <ProjectsVisual />,
  tasks: <div className="card-marketing h-64 flex items-center justify-center bg-gray-50 border-dashed border-2 border-gray-200 text-gray-400">Task Board View</div>,
  notifications: <div className="card-marketing h-64 flex items-center justify-center bg-gray-50 border-dashed border-2 border-gray-200 text-gray-400">Live Notification Feed</div>,
  email: <div className="card-marketing h-64 flex items-center justify-center bg-gray-50 border-dashed border-2 border-gray-200 text-gray-400">Email Templates</div>,
};

const FeaturesPage = () => {
  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Integrations") return { ...link, route: "/integrations" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Changelog") return { ...link, route: "/changelog" };
        if (link.label === "Security") return { ...link, route: "/security" };
        if (link.label === "Blog") return { ...link, route: "/blog" };
        if (link.label === "Help Center") return { ...link, route: "/help" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Careers") return { ...link, route: "/careers" };
        if (link.label === "FAQ") return { ...link, route: "/faq" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, id) => {
      e.preventDefault();
      smoothScroll(id);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right, .landing-page .section-animate"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white">
      <MarketingHeader
        navLinks={navLinks}
        onNavClick={handleNavClick}
        ctaTarget="cta"
        logoTarget="top"
      />

      <main>
        <section id="top" className="curved-section shell-sky section-animate relative min-h-screen flex items-center overflow-hidden pt-16 pb-20">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid lg:grid-cols-2 gap-20 items-center">
            <div className="reveal-left">
              <span className="section-eyebrow">Product Features</span>
              <h1 className="mt-6 mb-8">
                The PMS feature stack designed for <span className="text-blue-600">high-velocity teams.</span>
              </h1>
              <p className="text-lead mb-10 max-w-2xl">
                From authentication to delivery, each capability is built with reliability, role clarity, and real-time visibility in mind.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  className="btn-primary" 
                  onClick={(e) => handleNavClick(e, "authentication")}
                >
                  View Capabilities
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
                <Link to="/dashboard" className="btn-secondary">
                  Launch Demo
                </Link>
              </div>
            </div>
            <div className="reveal-right">
              <div className="card-marketing p-10 bg-navy text-sky relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <Sparkles className="w-32 h-32" />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-8">
                    <BadgeCheck className="w-6 h-6 text-sky" />
                    <span className="font-bold uppercase tracking-widest text-sm text-sky">Enterprise Ready</span>
                  </div>
                  <h3 className="text-3xl font-black mb-6 text-white">Production Hardened</h3>
                  <div className="space-y-6">
                    {[
                      { icon: ShieldCheck, text: "JWT & RBAC Security" },
                      { icon: Activity, text: "Real-time Sync" },
                      { icon: Bell, text: "Smart Notifications" }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-sky" />
                        </div>
                        <span className="font-bold text-white">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {featureSections.map((section, idx) => (
          <section
            key={section.id}
            id={section.id}
            className={`shell-${idx % 2 === 0 ? "white" : "sky"} section-animate`}
          >
            <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-20 items-center">
              <div className={idx % 2 !== 0 ? "lg:order-2 reveal-right" : "reveal-left"}>
                <span className="section-eyebrow">{section.label}</span>
                <h2 className="mt-6 mb-6">{section.title}</h2>
                <p className="text-body mb-8 text-xl">{section.description}</p>
                <div className="flex flex-wrap gap-2 mb-8">
                  {section.chips.map((chip) => (
                    <span key={chip} className="feature-pill">{chip}</span>
                  ))}
                </div>
                <ul className="space-y-4">
                  {section.highlights.map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-lg font-semibold text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={idx % 2 !== 0 ? "lg:order-1 reveal-left" : "reveal-right"}>
                {visuals[section.visual]}
              </div>
            </div>
          </section>
        ))}

        <section id="board" className="shell-sky section-animate">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
            <span className="section-eyebrow reveal">Interactive Board</span>
            <h2 className="reveal stagger-1 mt-6">Experience the <span className="text-navy">Workflow</span></h2>
          </div>
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto pb-10">
            <div className="flex gap-6 min-w-[1000px]">
              {boardColumns.map((column) => (
                <div key={column.id} className="flex-1 bg-gray-50/50 border border-gray-100 rounded-[32px] p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-xl font-black text-gray-900">{column.title}</h3>
                      <p className="text-xs text-gray-500 mt-1">{column.description}</p>
                    </div>
                    <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-sm font-bold shadow-sm">{column.cards.length}</span>
                  </div>
                  <div className="space-y-4">
                    {column.cards.map((card) => (
                      <div key={card.title} className="card-marketing p-5 !rounded-2xl border-gray-100 hover:shadow-lg">
                        <div className="text-sm font-bold text-gray-900 mb-4">{card.title}</div>
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-1 rounded text-[10px] font-black uppercase ${
                            card.priority === 'High' ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
                          }`}>{card.priority}</span>
                          <div className="flex -space-x-2">
                            {card.members.map(m => (
                              <div key={m} className="w-7 h-7 rounded-full bg-navy text-sky text-[10px] font-black flex items-center justify-center border-2 border-white">{m}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <MarketingTestimonials />

        <section id="cta" className="shell-navy text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8">Ready to take control of your projects?</h2>
            <p className="text-lead text-blue-100 mb-12">
              Join thousands of teams shipping faster with PMS.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-secondary">
                Get Started Now
              </Link>
              <Link to="/" className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                Back to Landing
              </Link>
            </div>
          </div>
        </section>
      </main>
      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default FeaturesPage;
